'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useComponentTranslations } from '@/app/components/LanguageProvider';
import CareerForm from '@/app/components/CareerForm';
import LocationMap from '@/app/components/LocationMap';
import PageHero from '@/app/components/PageHero';
import PageContainer from '@/app/components/PageContainer';
import PageSection from '@/app/components/PageSection';
import SectionTitle from '@/app/components/SectionTitle';
import GlassCard from '@/app/components/GlassCard';
import { useLocale } from 'next-intl';

interface Vacancy {
  id: number;
  title: string;
  description: string;
  requirements?: string;
  location?: string;
  salary?: string;
  employment_type?: string;
  status: string;
  created_at: string;
  country?: string;
  city?: string;
  lat?: number;
  lon?: number;
}

const formatLocation = (vacancy: Vacancy) => {
    if (vacancy.location) return vacancy.location;
    return [vacancy.city, vacancy.country].filter(Boolean).join(', ');
};

const stripHtml = (html: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
};

export default function CareersPage() {
    const { t, translationsLoaded } = useComponentTranslations('Careers');
    const ct = (key: string, fallback?: string) => {
        if (!translationsLoaded) return fallback ?? key;
        return t(key, fallback) ?? fallback ?? key;
    };
    const locale = useLocale();
    const isEn = locale.startsWith('en');

    const [vacancies, setVacancies] = useState<Vacancy[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedVacancy, setSelectedVacancy] = useState<Vacancy | null>(null);

    const fetchVacancies = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`/api/vacancies?status=active&locale=${locale}`);
            if (!response.ok) {
                console.error('Failed to fetch vacancies:', response.status);
                setVacancies([]);
                return;
            }
            const data = await response.json();
            if (!Array.isArray(data)) {
                console.error('Invalid vacancies data format:', data);
                setVacancies([]);
                return;
            }
            setVacancies(data);
        } catch (error) {
            console.error('Error fetching vacancies:', error);
            setVacancies([]);
        } finally {
            setLoading(false);
        }
    }, [locale]);

    useEffect(() => {
        (async () => {
            await fetchVacancies();
        })();
    }, [locale, fetchVacancies]);

    if (selectedVacancy) {
        return (
            <div className="min-h-screen text-white">
                <PageContainer>
                    <button
                        onClick={() => setSelectedVacancy(null)}
                        className="mb-4 mt-2 inline-flex items-center gap-2 text-sm text-blue-300 transition-colors hover:text-blue-200"
                    >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        {isEn ? 'Back to vacancies' : 'Назад к вакансиям'}
                    </button>
                    <header className="mb-6 md:mb-8">
                        <h1 className="text-2xl font-bold tracking-tight text-white md:text-3xl lg:text-4xl">
                            {selectedVacancy.title}
                        </h1>
                        <div className="mt-3 flex flex-wrap gap-2 text-xs text-white/60">
                            {formatLocation(selectedVacancy) && (
                                <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1">
                                    {formatLocation(selectedVacancy)}
                                </span>
                            )}
                            {selectedVacancy.salary && (
                                <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1">
                                    {selectedVacancy.salary}
                                </span>
                            )}
                            {selectedVacancy.employment_type && (
                                <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1">
                                    {selectedVacancy.employment_type}
                                </span>
                            )}
                        </div>
                    </header>
                </PageContainer>

                <PageSection>
                    <div className="grid gap-4">
                        <GlassCard>
                            <h2 className="text-base font-semibold text-white">{isEn ? 'Description' : 'Описание'}</h2>
                            <div className="prose prose-invert mt-3 max-w-none text-sm text-white/70" dangerouslySetInnerHTML={{ __html: selectedVacancy.description }} />
                        </GlassCard>
                        {selectedVacancy.requirements && (
                            <GlassCard>
                                <h2 className="text-base font-semibold text-white">{isEn ? 'Requirements' : 'Требования'}</h2>
                                <div className="prose prose-invert mt-3 max-w-none text-sm text-white/70" dangerouslySetInnerHTML={{ __html: selectedVacancy.requirements }} />
                            </GlassCard>
                        )}
                        {typeof selectedVacancy.lat === 'number' && typeof selectedVacancy.lon === 'number' && (
                            <GlassCard>
                                <h2 className="text-base font-semibold text-white">{isEn ? 'Location' : 'Местоположение'}</h2>
                                <div className="mt-3">
                                    <LocationMap
                                        lat={selectedVacancy.lat}
                                        lon={selectedVacancy.lon}
                                        city={selectedVacancy.city}
                                        country={selectedVacancy.country}
                                    />
                                </div>
                            </GlassCard>
                        )}
                        <GlassCard className="p-6 md:p-8">
                            <CareerForm vacancyId={selectedVacancy.id} vacancyTitle={selectedVacancy.title} />
                        </GlassCard>
                    </div>
                </PageSection>
            </div>
        );
    }

    return (
        <div className="min-h-screen text-white">
            <PageContainer>
                <PageHero
                    id="careers-hero"
                    title={ct('careersPageTitle') || (isEn ? 'Careers' : 'Карьера')}
                    description={ct('careersPageSubtitle') || (isEn ? 'Join the Dev48 team' : 'Присоединяйтесь к команде Dev48')}
                    badges={
                        isEn
                            ? ["Minsk / Remote", "Flexible hours", "Reviews twice a year"]
                            : ["Минск / Удалённо", "Гибкий график", "Review 2 раза в год"]
                    }
                    actions={[
                        {
                            href: "#open-positions",
                            label: isEn ? "View vacancies" : "Смотреть вакансии",
                            primary: true,
                            ctaLocation: "pagehero_careers_primary",
                        },
                        {
                            href: "#apply-form",
                            label: isEn ? "Send a CV" : "Отправить резюме",
                            ctaLocation: "pagehero_careers_secondary",
                        },
                    ]}
                />
            </PageContainer>

            <PageSection id="open-positions">
                <SectionTitle
                    title={isEn ? "Open positions" : "Открытые вакансии"}
                    description={
                        isEn
                            ? "We keep in touch with strong engineers even when there is no matching role."
                            : "Мы поддерживаем связь с сильными инженерами, даже если подходящей роли сейчас нет."
                    }
                />

                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-white" />
                    </div>
                ) : vacancies.length === 0 ? (
                    <div id="apply-form" className="scroll-mt-24">
                        <GlassCard className="p-6 md:p-8">
                            <p className="mb-6 text-sm text-white/60">
                                {isEn ? "No open vacancies at the moment" : "На данный момент нет открытых вакансий"}
                            </p>
                            <CareerForm />
                        </GlassCard>
                    </div>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {vacancies.map((vacancy) => (
                            <GlassCard key={vacancy.id} className="flex h-full flex-col">
                                <h3 className="text-base font-semibold text-white">{vacancy.title}</h3>
                                <div className="mt-2 space-y-1 text-sm text-white/50">
                                    {formatLocation(vacancy) && <p>{formatLocation(vacancy)}</p>}
                                    {vacancy.salary && <p>{vacancy.salary}</p>}
                                    {vacancy.employment_type && <p>{vacancy.employment_type}</p>}
                                </div>
                                <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-white/60">
                                    {stripHtml(vacancy.description)}
                                </p>
                                <button
                                    onClick={() => setSelectedVacancy(vacancy)}
                                    className="mt-auto pt-4 text-left text-sm font-medium text-blue-300 transition-colors hover:text-blue-200"
                                >
                                    {isEn ? "Details and apply →" : "Подробнее и откликнуться →"}
                                </button>
                            </GlassCard>
                        ))}
                    </div>
                )}
            </PageSection>

            {vacancies.length > 0 && (
                <PageSection id="apply-form">
                    <SectionTitle
                        title={isEn ? "No suitable vacancy?" : "Не нашли подходящую вакансию?"}
                        description={
                            isEn
                                ? "Send your CV — we keep in touch with strong engineers for future projects."
                                : "Пришлите резюме — мы поддерживаем связь с сильными инженерами для будущих проектов."
                        }
                    />
                    <GlassCard className="p-6 md:p-8">
                        <CareerForm />
                    </GlassCard>
                </PageSection>
            )}
        </div>
    );
}
